import * as stylex from '@stylexjs/stylex'
import { memo, type ComponentPropsWithoutRef, type ElementType, type ForwardedRef, type ReactNode } from 'react'
import isEqual from 'react-fast-compare'
import { colors } from '../tokens/colors.stylex'
import { makeExtendStyle, type ExtendStyleProps } from '../utils/makeExtendStyle/makeExtendStyle'

const _baseStyles = stylex.create({
  root: {
    outline: 'none',
    color: 'inherit',
    display: 'table-row',
    verticalAlign: 'middle',
  },
  hover: {
    backgroundColor: {
      ':hover': colors.grey50,
    },
    transitionDuration: '50ms',
    transitionProperty: 'background-color',
    transitionTimingFunction: 'ease-in-out',
  },
  selected: {
    backgroundColor: {
      'default': colors.blue100,
      ':hover': colors.blue300,
    },
  },
})

export type TableRowProps<RootComponent extends ElementType = 'tr', TableRowElement extends HTMLElement = HTMLTableRowElement> = {
  /** 自訂根元素標籤 */
  'component'?: RootComponent
  /** 子內容（通常為 TableCell） */
  'children'?: ReactNode
  /**
   * 是否啟用滑鼠懸停高亮效果
   * @default false
   */
  'hover'?: boolean
  /**
   * 是否為選取狀態（藍色背景）
   * @default false
   */
  'selected'?: boolean
  /** 自訂根元素的 StyleX 樣式 */
  'rootStyle'?: stylex.StyleXStyles
  /** 根元素的 ref */
  'rootRef'?: ForwardedRef<TableRowElement>
  /** 測試用 data 屬性 */
  'data-testid'?: string
} & ExtendStyleProps & ComponentPropsWithoutRef<RootComponent>

const TableRowComponent = <RootComponent extends ElementType = 'tr', TableRowElement extends HTMLElement = HTMLTableRowElement>(
  props: TableRowProps<RootComponent, TableRowElement>,
) => {
  const {
    component = 'tr',
    hover = false,
    selected = false,
    extendStyles,
    rootStyle,
    rootRef,
    ...rest
  } = makeExtendStyle(props)

  const Component = component as ElementType
  const defaultComponent = 'tr'

  return (
    <Component
      ref={rootRef}
      role={component === defaultComponent ? undefined : 'row'}
      {...stylex.props(
        _baseStyles.root,
        hover && _baseStyles.hover,
        selected && _baseStyles.selected,
        rootStyle,
        extendStyles,
      )}
      {...rest}
    />
  )
}

/**
 * TableRow 是表格列元件，支援懸停高亮與選取狀態。
 */
export const TableRow = memo(TableRowComponent, isEqual) as typeof TableRowComponent
